import { noop, useIntl } from '@capsicum/common';
import { Button, GenericDrawer, ModalService } from '@capsicum/common-web';
import { useWalletConnectAnalytics } from '@capsicum/socios-core';
import { type WalletConnectConfirmActionProps, type WalletConnectSession } from '@socios-blk/core';
import { useState } from 'react';
import styled from 'styled-components';

import { useWalletConnectConfirmationMessage } from './useWalletConnectConfirmationMessage';

export const WalletConnectConfirmationModalFooter = ({
	close,
	confirmActionProps,
	onApprove,
	onDeny
}: {
	close: () => void;
	confirmActionProps: WalletConnectConfirmActionProps;
	onApprove: (callback: () => void) => void;
	onDeny?: () => void;
}) => {
	const { $t } = useIntl();
	const [isActionLoading, setActionLoading] = useState(false);
	const { sendWalletConnectAnalytics } = useWalletConnectAnalytics(confirmActionProps);

	return (
		<S.ButtonWrapper>
			<Button
				color="neutral"
				disabled={isActionLoading}
				fullWidth
				testID="wallet-connect-action-confirm-modal-deny-button"
				text={$t('global.button.reject')}
				variant="outlined"
				onClick={() => {
					onDeny?.();
					close();
				}}
			/>
			<Button
				fullWidth
				isLoading={isActionLoading}
				testID="wallet-connect-action-confirm-modal-approve-button"
				text={$t('global.button.confirm')}
				onClick={() => {
					setActionLoading(true);
					onApprove((isSuccess?: boolean) => {
						isSuccess && sendWalletConnectAnalytics();
						setActionLoading(false);
						close();
					});
				}}
			/>
		</S.ButtonWrapper>
	);
};

export const useWalletConnectConfirmationModal = (walletConnectSessions: Array<WalletConnectSession>) => {
	const { getMessage } = useWalletConnectConfirmationMessage(walletConnectSessions);

	const confirmAction = (props: WalletConnectConfirmActionProps) => {
		const { onApprove, onDeny = noop } = props;
		const Body = () => <S.ModalBody>{getMessage(props)}</S.ModalBody>;

		ModalService.addModal({
			detached: true,
			isClosable: false,
			renderComponent: () => (
				<GenericDrawer testID="wallet-connect-confirmation-modal">
					<Body />
				</GenericDrawer>
			),
			renderFooter: ({ close }) => (
				<WalletConnectConfirmationModalFooter
					close={close}
					confirmActionProps={props}
					onApprove={onApprove}
					onDeny={onDeny}
				/>
			)
		});
	};

	return {
		confirmAction
	};
};

namespace S {
	export const ButtonWrapper = styled.div`
		display: flex;
		flex-direction: 'column';
		justify-content: center;
		align-items: center;
		margin-top: ${({ theme }) => theme.spacings.large};
		gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const ModalBody = styled.div`
		padding: ${({ theme }) => theme.spacings.medium};
	`;
}
